import { useState } from 'react';
import { useRequestEmailChange, useCancelEmailChange } from '@/api/hooks/useProfile';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getErrorMessage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import { EnvelopeIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { AccountProfileResponse } from '@/api/types';

interface EmailSectionProps {
    profile: AccountProfileResponse | null;
    isLoading: boolean;
    error: Error | null;
    onRefresh: () => void;
}

function formatExpiryTime(expiresAt: string): string {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) return 'Expired';

    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 60) return `${diffMins} minutes`;

    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    if (remainingMins > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${remainingMins} min`;
    }
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
}

export function EmailSection({ profile, isLoading, error, onRefresh }: EmailSectionProps) {
    const requestEmailChange = useRequestEmailChange();
    const cancelEmailChange = useCancelEmailChange();

    const [isEditing, setIsEditing] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [formError, setFormError] = useState<string | null>(null);

    const user = profile?.user;
    const pendingChange = profile?.pendingEmailChange;

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setFormError(null);

        if (!newEmail.trim()) {
            setFormError('Email is required');
            return;
        }

        try {
            await requestEmailChange.mutateAsync({ newEmail: newEmail.trim() });
            toast.success('Confirmation email sent to your current email address');
            setIsEditing(false);
            setNewEmail('');
            onRefresh();
        } catch (err) {
            const message = getErrorMessage(err);
            setFormError(message);
        }
    }

    async function handleCancel() {
        try {
            await cancelEmailChange.mutateAsync();
            toast.success('Email change cancelled');
            onRefresh();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    }

    if (isLoading) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                <div className="animate-pulse">
                    <div className="h-6 w-24 rounded-sm bg-gray-200 dark:bg-dark" />
                    <div className="mt-4 h-4 w-48 rounded-sm bg-gray-200 dark:bg-dark" />
                </div>
            </div>
        );
    }

    if (error) {
        return <ErrorAlert message={getErrorMessage(error)} />;
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
            <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gray-100 p-2 dark:bg-dark">
                    <EnvelopeIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Email Address</h2>
            </div>

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Your email is used for account notifications, security alerts, and password recovery.
            </p>

            <div className="mt-4">
                {pendingChange ? (
                    <div className="space-y-4">
                        <div className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Current email:</span> {user?.email}
                        </div>

                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                            <div className="flex items-start gap-3">
                                <ClockIcon className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
                                <div className="flex-1">
                                    <p className="font-medium text-amber-800 dark:text-amber-300">
                                        Email change pending
                                    </p>
                                    <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                                        {pendingChange.status === 'awaiting_confirmation' ? (
                                            <>
                                                Check <span className="font-medium">{user?.email}</span> for a confirmation link.
                                            </>
                                        ) : (
                                            <>
                                                Check <span className="font-medium">{pendingChange.pendingEmail}</span> for a verification link.
                                            </>
                                        )}
                                    </p>
                                    <p className="mt-1 text-sm text-amber-600 dark:text-amber-500">
                                        Changing to: <span className="font-medium">{pendingChange.pendingEmail}</span>
                                    </p>
                                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                                        Expires in {formatExpiryTime(pendingChange.expiresAt)}
                                    </p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancel}
                                    isLoading={cancelEmailChange.isPending}
                                    className="text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/30"
                                >
                                    <XMarkIcon className="mr-1 h-4 w-4" />
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="currentEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Current email
                            </label>
                            <p className="mt-1 text-gray-600 dark:text-gray-400">{user?.email}</p>
                        </div>

                        <Input
                            id="newEmail"
                            type="email"
                            label="New email address"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter your new email address"
                            disabled={requestEmailChange.isPending}
                            autoFocus
                        />

                        {formError && <ErrorAlert message={formError} />}

                        <div className="flex gap-3">
                            <Button
                                type="submit"
                                isLoading={requestEmailChange.isPending}
                                disabled={requestEmailChange.isPending || !newEmail.trim()}
                            >
                                Send Confirmation
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setIsEditing(false);
                                    setNewEmail('');
                                    setFormError(null);
                                }}
                                disabled={requestEmailChange.isPending}
                            >
                                Cancel
                            </Button>
                        </div>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            You'll need to verify from both your current and new email addresses. Each link expires in 1 hour.
                        </p>
                    </form>
                ) : (
                    <div className="flex items-center justify-between">
                        <p className="text-gray-700 dark:text-gray-300">{user?.email}</p>
                        <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
                            Change Email
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
